import { useAtom } from "jotai";
import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoginData } from "@/components/Portal/UserAccount/Security/LoginData";
import { MagicLinkLoginSetting } from "@/components/Portal/UserAccount/Security/MagicLinkLoginSetting";
import { useIsInSecuritySubFlow } from "@/hooks/useIsInSecuritySubFlow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { userAccountStateAtom } from "@/utils/atoms";

/**
 * Login-tab layout, which differs by viewport:
 *
 * - Desktop/tablet: E-Mail, Passwort, Magic-Link and the 2FA section stack in
 *   one column (left of TwoColumnLayout). The nested route (`<Outlet/>`) drives
 *   the 2FA flow inline below the login cards; every step change brings the
 *   2FA card into view and moves focus into it.
 * - Mobile: every 2FA step is its own screen. Only the active card is rendered —
 *   without the sibling cards — and `UserAccount` hides the account header,
 *   banner and tab strip to match.
 */
export const SecurityView = () => {
  const isMobile = useIsMobile();
  const [userAccountState] = useAtom(userAccountStateAtom);
  const isInSubFlow = useIsInSecuritySubFlow();
  const { pathname } = useLocation();
  const subFlowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isInSubFlow) {
      return;
    }

    // Deferred a frame: the 2FA code dialog restores focus to the tab strip
    // on unmount (in a microtask), which would otherwise win.
    const frame = requestAnimationFrame(() => {
      const subFlow = subFlowRef.current;

      if (!subFlow) {
        return;
      }

      // Leaves an autofocused field of the new step (e.g. the code input) alone.
      if (!subFlow.contains(document.activeElement)) {
        subFlow.focus({ preventScroll: true });
      }
      subFlow.scrollIntoView?.({ block: "start" });
    });

    return () => cancelAnimationFrame(frame);
  }, [isInSubFlow, pathname]);

  if (isMobile && isInSubFlow) {
    return <Outlet />;
  }

  // Editing the E-Mail/Passwort card is likewise a standalone mobile screen:
  // LoginData keeps only the card being edited, so the Magic-Link card has to
  // go with them.
  const isStandaloneMobileEdit =
    isMobile &&
    (userAccountState.editingMail || userAccountState.editingPassword);

  return (
    <div className="flex flex-col gap-6">
      {isStandaloneMobileEdit ? (
        <LoginData />
      ) : (
        <TwoColumnLayout
          rows={[
            {
              left: (
                <div className="flex w-full flex-col gap-6">
                  <LoginData />
                  <MagicLinkLoginSetting />
                  <div
                    // Below desktop the fixed 56px portal header covers the top.
                    className="scroll-mt-15 outline-none desktop:scroll-mt-6"
                    ref={subFlowRef}
                    tabIndex={-1}
                  >
                    <Outlet />
                  </div>
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};
